/**
 * Payment runs: prepare, approve, release.
 *
 * The findings panel is the point of this screen. It is the same function the
 * release gate calls, so what a person reads here is exactly what will stop
 * them — rather than discovering at the last step that the thing they spent an
 * afternoon preparing was never releasable.
 *
 * Nothing here moves money. Releasing records that a person went to the bank
 * and instructed it; the statement comes back and reconciliation closes the
 * loop. That boundary is deliberate.
 *
 * The list says whose move each run is waiting on; a run opens beside it with
 * its findings first, then its payees, then the bills it could still take.
 */

import { useState, type ReactNode } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, formatInr } from '../api'
import { useAuth } from '../auth'
import { ErrorNote, Field, Loading, Note, Pill, Stat } from '../components/ui'
import { Pager, usePaged } from '../components/Pager'
import { SidePanel, Tabs, matches, rowProps } from '../components/SidePanel'

interface Finding {
  key: string
  detail: string
  blocking: boolean
  line_no: number | null
}

interface Allocation {
  vendor_invoice_id: string
  invoice_reference: string
  amount_paise: number
  override_reason: string | null
}

interface Payment {
  id: string
  line_no: number
  party_id: string
  party_name: string
  beneficiary_masked: string | null
  amount_paise: number
  status: string
  instrument_ref: string | null
  journal_entry_id: string | null
  allocations: Allocation[]
}

interface Run {
  id: string
  reference: string
  payment_date: string
  bank_account_id: string
  bank_account_name: string
  method: string
  status: string
  version: number
  total_paise: number
  released_at: string | null
  approval_state: string
  payments: Payment[]
  findings: Finding[]
}

interface OpenInvoice {
  invoice_id: string
  reference: string
  party_id: string
  party_name: string
  due_date: string
  days_overdue: number
  outstanding_paise: number
  msme_clock: boolean
}

interface BankAccount {
  id: string
  name: string
}

const METHODS = ['bank_transfer', 'upi', 'cheque', 'cash']
const today = () => new Date().toISOString().slice(0, 10)

type Tab = 'progress' | 'released' | 'all'

export default function Payments() {
  const { can } = useAuth()
  const queryClient = useQueryClient()
  const [open, setOpen] = useState<string | null>(null)
  const [starting, setStarting] = useState(false)
  const [tab, setTab] = useState<Tab>('progress')
  const [search, setSearch] = useState('')
  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['runs'] })
    void queryClient.invalidateQueries({ queryKey: ['open-invoices'] })
    void queryClient.invalidateQueries({ queryKey: ['ageing'] })
  }

  const runs = useQuery({
    queryKey: ['runs'],
    queryFn: () => api.get<Run[]>('/payables/runs'),
  })
  const banks = useQuery({
    queryKey: ['bank-accounts'],
    queryFn: () => api.get<BankAccount[]>('/banking/accounts'),
    enabled: can('org.bank_account.view'),
  })

  const [form, setForm] = useState({
    payment_date: today(),
    bank_account_id: '',
    method: 'bank_transfer',
  })

  const create = useMutation({
    mutationFn: () => api.post<Run>('/payables/runs', form),
    onSuccess: (run) => {
      setStarting(false)
      setOpen(run.id)
      refresh()
    },
  })

  const rows = runs.data ?? []
  const released = (r: Run) => r.status === 'released' || !!r.released_at
  const shown = rows.filter(
    (r) =>
      (tab === 'all' || (tab === 'released' ? released(r) : !released(r))) &&
      matches(
        search,
        r.reference,
        r.bank_account_name,
        r.method,
        ...r.payments.map((p) => p.party_name),
      ),
  )
  const paged = usePaged(shown, `${tab}|${search}`)

  if (runs.isLoading) return <Loading what="Loading payment runs" />

  const step = (run: Run) => {
    const blocked = run.findings.some((f) => f.blocking)
    if (run.status === 'draft')
      return run.payments.length === 0
        ? { label: 'Add payees', tone: can('ap.payment.edit') ? 'turn' : 'draft' }
        : can('ap.payment.submit')
          ? { label: 'Send for approval', tone: 'turn' }
          : { label: 'Being prepared', tone: 'draft' }
    if (run.status === 'submitted')
      return can('ap.payment.approve')
        ? { label: 'Waiting for you', tone: 'turn', detail: run.approval_state }
        : { label: 'With a checker', tone: 'submitted', detail: run.approval_state }
    if (run.status === 'approved')
      return blocked
        ? { label: 'Blocked', tone: 'returned', detail: 'see the findings' }
        : can('ap.payment.release')
          ? { label: 'Pay at the bank, then release', tone: 'turn' }
          : { label: 'Approved, not released', tone: 'advisory' }
    if (released(run)) return { label: 'Released', tone: 'approved' }
    return { label: run.status.replace(/_/g, ' '), tone: run.status }
  }
  const stepCell = (run: Run) => {
    const s = step(run)
    return (
      <div className="next-step">
        <span className={`pill ${s.tone}`}>{s.label}</span>
        {'detail' in s && s.detail && <span className="faint">{s.detail}</span>}
      </div>
    )
  }

  const inProgress = rows.filter((r) => !released(r))
  const mine = rows.filter((r) => step(r).tone === 'turn').length
  const blocked = rows.filter((r) => !released(r) && r.findings.some((f) => f.blocking))
  const doneRuns = rows.filter(released)
  const opened = rows.find((r) => r.id === open)

  return (
    <>
      <ErrorNote error={runs.error} />

      <div className="bank-toolbar">
        {can('ap.payment.prepare') && (
          <button className="primary" onClick={() => setStarting(true)}>
            Start a run
          </button>
        )}
      </div>

      <div className="grid">
        <Stat label="Waiting for you" value={String(mine)} note="to prepare, approve or release" />
        <Stat
          label="In progress"
          value={String(inProgress.length)}
          note={`${formatInr(inProgress.reduce((t, r) => t + r.total_paise, 0))} not yet released`}
        />
        <Stat
          label="Blocked"
          value={String(blocked.length)}
          note={blocked.length ? 'the findings say what stops them' : 'nothing is stopping a run'}
        />
        <Stat
          label="Released"
          value={String(doneRuns.length)}
          note={`${formatInr(doneRuns.reduce((t, r) => t + r.total_paise, 0))} instructed at the bank`}
        />
      </div>

      <section className="card wide">
        <header className="appr-toolbar">
          <Tabs
            tabs={[
              { key: 'progress', label: 'In progress', count: inProgress.length },
              { key: 'released', label: 'Released', count: doneRuns.length },
              { key: 'all', label: 'All runs', count: rows.length },
            ]}
            value={tab}
            onChange={setTab}
          />
          <div className="appr-filters">
            <input
              type="search"
              placeholder="Reference, account, payee"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              aria-label="Search runs"
            />
          </div>
        </header>
        <div className="body tight">
          {shown.length === 0 ? (
            <div className="empty">
              {rows.length === 0
                ? 'No payment runs yet.'
                : tab === 'progress' && !search
                  ? 'Nothing in progress — every run is released.'
                  : 'No run matches that.'}
            </div>
          ) : (
            <table className="appr-table">
              <thead>
                <tr>
                  <th>Run</th>
                  <th>Paying on</th>
                  <th>Method</th>
                  <th className="num">Payees</th>
                  <th className="num">Total</th>
                  <th>Status</th>
                  <th>Next step</th>
                </tr>
              </thead>
              <tbody>
                {paged.rows.map((run) => (
                  <tr key={run.id} {...rowProps(() => setOpen(run.id), open === run.id)}>
                    <td>
                      <div className="strong">{run.bank_account_name}</div>
                      <div className="cell-sub ref">{run.reference}</div>
                    </td>
                    <td className="muted date">{run.payment_date}</td>
                    <td className="muted">{run.method.replace(/_/g, ' ')}</td>
                    <td className="num">{run.payments.length}</td>
                    <td className="num">
                      <strong>{formatInr(run.total_paise)}</strong>
                    </td>
                    <td>
                      <Pill value={run.status} />
                    </td>
                    <td>{stepCell(run)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        <Pager page={paged.page} pages={paged.pages} onPage={paged.setPage} />
      </section>

      {opened && (
        <RunDetail
          key={opened.id}
          run={opened}
          badge={stepCell(opened)}
          onClose={() => setOpen(null)}
          onDone={refresh}
        />
      )}

      {starting && (
        <SidePanel
          kicker="Payment run"
          title="Start a run"
          sub="nothing leaves until somebody else approves it"
          onClose={() => setStarting(false)}
        >
          <ErrorNote error={create.error} />
          <form
            onSubmit={(e) => {
              e.preventDefault()
              create.mutate()
            }}
          >
            <Field label="Paying on" caption="the journal date of every payment in it">
              <input
                type="date"
                value={form.payment_date}
                onChange={(e) => setForm({ ...form, payment_date: e.target.value })}
                required
              />
            </Field>
            <Field label="From account">
              <select
                value={form.bank_account_id}
                onChange={(e) => setForm({ ...form, bank_account_id: e.target.value })}
                required
              >
                <option value="">Choose…</option>
                {(banks.data ?? []).map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.name}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Method">
              <select
                value={form.method}
                onChange={(e) => setForm({ ...form, method: e.target.value })}
              >
                {METHODS.map((m) => (
                  <option key={m} value={m}>
                    {m.replace(/_/g, ' ')}
                  </option>
                ))}
              </select>
            </Field>
            <div className="row" style={{ marginTop: 12 }}>
              <div className="shrink">
                <button className="primary" type="submit" disabled={create.isPending}>
                  Start run
                </button>
              </div>
            </div>
            <Note kind="info">
              The run opens next, so its payees can be added from the bills that are due.
            </Note>
          </form>
        </SidePanel>
      )}
    </>
  )
}

function RunDetail({
  run,
  badge,
  onClose,
  onDone,
}: {
  run: Run
  badge: ReactNode
  onClose: () => void
  onDone: () => void
}) {
  const { can } = useAuth()
  const editable = run.status === 'draft'

  const dueBills = useQuery({
    queryKey: ['open-invoices'],
    queryFn: () => api.get<OpenInvoice[]>('/payables/open-invoices'),
    enabled: editable && can('ap.payment.edit'),
  })

  const [picked, setPicked] = useState<Record<string, string>>({})
  const [reasons, setReasons] = useState<Record<string, string>>({})

  const addPayee = useMutation({
    mutationFn: (partyId: string) => {
      const allocations = (dueBills.data ?? [])
        .filter((b) => b.party_id === partyId && picked[b.invoice_id])
        .map((b) => ({
          vendor_invoice_id: b.invoice_id,
          amount_paise: Math.round(Number(picked[b.invoice_id]) * 100),
          override_reason: reasons[b.invoice_id] || null,
        }))
      return api.post<Run>(`/payables/runs/${run.id}/payments`, {
        party_id: partyId,
        allocations,
      })
    },
    onSuccess: () => {
      setPicked({})
      setReasons({})
      onDone()
    },
  })
  const submit = useMutation({
    mutationFn: () => api.post<Run>(`/payables/runs/${run.id}/submit`),
    onSuccess: onDone,
  })
  const decide = useMutation({
    mutationFn: (approve: boolean) =>
      api.post<Run>(`/payables/runs/${run.id}/decide?approve=${approve}`),
    onSuccess: onDone,
  })
  const release = useMutation({
    mutationFn: () => api.post<Run>(`/payables/runs/${run.id}/release`),
    onSuccess: onDone,
  })

  const blocking = run.findings.filter((f) => f.blocking)
  const flags = run.findings.filter((f) => !f.blocking)

  // Bills grouped by payee, so somebody can settle three at once the way they
  // would on a vendor statement.
  const byParty = new Map<string, OpenInvoice[]>()
  for (const bill of dueBills.data ?? []) {
    const already = run.payments.some((p) => p.party_id === bill.party_id)
    if (already) continue
    byParty.set(bill.party_id, [...(byParty.get(bill.party_id) ?? []), bill])
  }

  return (
    <SidePanel
      kicker="Payment run"
      title={run.bank_account_name}
      sub={
        <>
          <span className="ref">{run.reference}</span> · paying on {run.payment_date} by{' '}
          {run.method.replace(/_/g, ' ')}
        </>
      }
      amount={formatInr(run.total_paise)}
      badge={badge}
      wide
      onClose={onClose}
      foot={
        <>
          <ErrorNote error={addPayee.error ?? submit.error ?? decide.error ?? release.error} />
          {editable && can('ap.payment.submit') && (
            <button
              className="primary"
              disabled={submit.isPending || run.payments.length === 0}
              onClick={() => submit.mutate()}
            >
              Send for approval
            </button>
          )}
          {run.status === 'submitted' && can('ap.payment.approve') && (
            <>
              <button
                className="primary"
                disabled={decide.isPending}
                onClick={() => decide.mutate(true)}
              >
                Approve
              </button>
              <button disabled={decide.isPending} onClick={() => decide.mutate(false)}>
                Reject
              </button>
            </>
          )}
          {run.status === 'submitted' && !can('ap.payment.approve') && (
            <span className="faint">With a checker — nothing for you to do.</span>
          )}
          {run.status === 'approved' && can('ap.payment.release') && (
            <button
              className="primary"
              disabled={release.isPending || blocking.length > 0}
              title={blocking.length > 0 ? 'The findings above stop this run' : undefined}
              onClick={() => release.mutate()}
            >
              Mark released
            </button>
          )}
        </>
      }
    >
      {(blocking.length > 0 || flags.length > 0) && (
        <section>
          <h3>Findings</h3>
          {blocking.length > 0 && (
            <Note kind="error">
              <strong>This run cannot go out yet.</strong>
              <ul>
                {blocking.map((f, i) => (
                  <li key={i}>
                    {f.line_no ? `Line ${f.line_no}: ` : ''}
                    {f.detail}
                  </li>
                ))}
              </ul>
            </Note>
          )}
          {flags.map((f, i) => (
            <Note key={i} kind="warn">
              {f.line_no ? `Line ${f.line_no}: ` : ''}
              {f.detail}
            </Note>
          ))}
        </section>
      )}

      <section>
        <dl className="appr-facts">
          <div>
            <dt>Status</dt>
            <dd>
              <Pill value={run.status} />
            </dd>
          </div>
          <div>
            <dt>Payees</dt>
            <dd>{run.payments.length}</dd>
          </div>
          <div>
            <dt>Approval</dt>
            <dd>{run.approval_state}</dd>
          </div>
          {run.released_at && (
            <div>
              <dt>Released</dt>
              <dd>{new Date(run.released_at).toLocaleString()}</dd>
            </div>
          )}
        </dl>
        {run.status === 'approved' && (
          <Note kind="info">
            Releasing records that you instructed the bank. This system does not move money — you
            pay at the bank, the statement comes back, and reconciliation closes the loop.
          </Note>
        )}
      </section>

      <section>
        <h3>Payees</h3>
        {run.payments.length === 0 ? (
          <div className="faint">
            Nobody yet.{' '}
            {editable && can('ap.payment.edit')
              ? byParty.size > 0
                ? 'Add them from the bills below.'
                : 'No posted bill is waiting to be paid, so there is nobody to add.'
              : ''}
          </div>
        ) : (
          <div className="scroll-x">
            <table className="appr-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Payee</th>
                  <th>Into</th>
                  <th>Settles</th>
                  <th className="num">Amount</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {run.payments.map((payment) => (
                  <tr key={payment.id}>
                    <td className="muted">{payment.line_no}</td>
                    <td className="strong">{payment.party_name}</td>
                    <td className="mono">
                      {payment.beneficiary_masked ?? <span className="faint">—</span>}
                    </td>
                    <td className="muted">
                      {payment.allocations.map((a) => (
                        <div key={a.vendor_invoice_id}>
                          <span className="ref">{a.invoice_reference}</span>{' '}
                          {formatInr(a.amount_paise)}
                          {a.override_reason && (
                            <div className="faint">paid anyway: {a.override_reason}</div>
                          )}
                        </div>
                      ))}
                    </td>
                    <td className="num">
                      <strong>{formatInr(payment.amount_paise)}</strong>
                    </td>
                    <td>
                      <Pill value={payment.status} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {editable && can('ap.payment.edit') && byParty.size > 0 && (
        <section>
          <h3>
            Add a payee <span className="hint">· bills that are due, by vendor</span>
          </h3>
          {[...byParty.entries()].map(([partyId, bills]) => (
            <div key={partyId} className="card" style={{ marginBottom: 12 }}>
              <div className="scroll-x">
                <table className="appr-table">
                  <thead>
                    <tr>
                      <th>{bills[0].party_name}</th>
                      <th>Due</th>
                      <th className="num">Overdue</th>
                      <th className="num">Outstanding</th>
                      <th className="num" style={{ width: 150 }}>
                        Paying (₹)
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {bills.map((bill) => (
                      <tr key={bill.invoice_id}>
                        <td className="ref">
                          {bill.reference}
                          {bill.msme_clock && <div className="faint">MSME</div>}
                        </td>
                        <td className="muted date">{bill.due_date}</td>
                        <td className="num">
                          {bill.days_overdue > 0 ? `${bill.days_overdue}d` : '—'}
                        </td>
                        <td className="num">{formatInr(bill.outstanding_paise)}</td>
                        <td>
                          <input
                            className="num"
                            value={picked[bill.invoice_id] ?? ''}
                            placeholder={String(bill.outstanding_paise / 100)}
                            aria-label={`Paying against ${bill.reference}`}
                            onChange={(e) =>
                              setPicked({ ...picked, [bill.invoice_id]: e.target.value })
                            }
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div style={{ padding: '10px 12px' }}>
                <button
                  className="small primary"
                  disabled={addPayee.isPending}
                  onClick={() => addPayee.mutate(partyId)}
                >
                  Add {bills[0].party_name}
                </button>
              </div>
            </div>
          ))}
        </section>
      )}
    </SidePanel>
  )
}
